import React, { useRef, useState } from 'react';
import { Box, Button, ButtonGroup } from '@mui/material';

import { degreesToSlope, slopeToDegrees } from '@paragon/geometry-library';
import { AngleField, DistanceField } from '@paragon/material';

import { getProfileIsClosed } from '../../../../../../library/profiles/profileManipulation';
import {
  addProfileLine,
  clearProfile,
  closeProfile,
  removeLineFromProfile,
} from '../../../../../../store/actions/profileActions';
import { resetMousyInsertionProfile } from '../../../../../../store/mousyInsertionProfileStore';
import { getSingleSelectedItemEntryOrNull } from '../../../../../../store/selectors/getSelectedItemSelector';
import { getInsertingProfileOrNull } from '../../../../../../store/selectors/profileSelectors';
import { useAppDispatch, useAppSelector } from '../../../../../hooks';
import { LabeledBox } from '../../../properties/LabeledBox';
import { ProfileJointEditor } from './ProfileJointEditor';
import WebJointEditor from './WebJointEditor';
import WebJointSegmentEditor from './WebJointSegmentEditor';

const evenSx = { display: 'flex', flex: 1, padding: 1, justifyContent: 'space-evenly' } as const;

export function ProfileCreator() {
  const dispatch = useAppDispatch();

  const profile = useAppSelector(getInsertingProfileOrNull);
  const selectedEntry = useAppSelector(getSingleSelectedItemEntryOrNull);

  const [distance, setDistance] = useState(96);
  const [slope, setSlope] = useState(0);
  const [forceShowProfileTools, setForceShowProfileTools] = useState<boolean>(false);

  const distanceInput = useRef<HTMLInputElement>(null);

  const focusAndSelectDistance = () => {
    if (distanceInput.current) {
      distanceInput.current.focus();
      distanceInput.current.select();
    }
  };

  const profileTools =
    forceShowProfileTools || !getProfileIsClosed(profile) ? (
      <LabeledBox title="Profile Line">
        <Box sx={evenSx}>
          <DistanceField
            label="Distance"
            value={distance}
            onSubmit={(newDistance, { finalizing }) => {
              setDistance(newDistance);
              if (finalizing) {
                dispatch(addProfileLine(newDistance, slope));
                focusAndSelectDistance();
              }
            }}
            sx={{ mx: 1 }}
            inputRef={distanceInput}
            blurOnEnter={false}
          />
          <AngleField
            label="Slope"
            defaultDisplayFormat="slope"
            sx={{ mx: 1 }}
            value={slopeToDegrees(slope)}
            onSubmit={(degrees, { finalizing }) => {
              const newSlope = degreesToSlope(degrees);
              setSlope(newSlope);
              if (finalizing) {
                dispatch(addProfileLine(distance, newSlope));
                focusAndSelectDistance();
              }
            }}
            blurOnEnter={false}
          />
        </Box>

        <ButtonGroup variant="outlined" size="small" sx={{ p: 1 }} fullWidth>
          <Button
            onClick={() => {
              dispatch(addProfileLine(distance, slope));
              focusAndSelectDistance();
            }}
          >
            Add Line
          </Button>
          <Button
            onClick={() => {
              dispatch(removeLineFromProfile());
              focusAndSelectDistance();
            }}
          >
            Back Up
          </Button>
          <Button
            onClick={() => {
              dispatch(closeProfile());
            }}
          >
            Close Profile
          </Button>
          <Button
            onClick={() => {
              dispatch(clearProfile());
              focusAndSelectDistance();

              resetMousyInsertionProfile();
            }}
          >
            Clear Profile
          </Button>
        </ButtonGroup>
      </LabeledBox>
    ) : (
      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-around', mt: 3 }}>
        <Button
          variant="outlined"
          sx={{ width: 245 }}
          onClick={() => {
            setForceShowProfileTools(true);

            dispatch(clearProfile());
            focusAndSelectDistance();

            resetMousyInsertionProfile();
          }}
        >
          Clear Profile and Start Drawing
        </Button>
        <Button
          variant="outlined"
          sx={{ width: 245 }}
          onClick={() => {
            setForceShowProfileTools(true);
          }}
        >
          Customize Existing Profile
        </Button>
      </Box>
    );

  let tool;
  if (selectedEntry?.type === 'webJointSegment') {
    tool = (
      <LabeledBox title="Selected Line">
        <WebJointSegmentEditor />
      </LabeledBox>
    );
  } else if (selectedEntry?.type === 'profileJoint') {
    tool = (
      <LabeledBox title="Selected Joint">
        <ProfileJointEditor />
      </LabeledBox>
    );
  } else if (selectedEntry?.type === 'webJoint') {
    tool = (
      <LabeledBox title="Selected Web Joint">
        <WebJointEditor />
      </LabeledBox>
    );
  } else {
    tool = null;
  }

  return (
    <>
      {profileTools}
      {tool}
    </>
  );
}
